// Copyright ©️ 2026 eVolve MEP, LLC

import type { PreferenceValueJsonString } from '@evolvemep/api-ts';

import type {
  UserPreferenceKey,
  UserPreferenceValue,
} from 'hooks/typeCacheHooks/useUserPreferences/useUserPreferences';

export const parseUserPreferenceValueJson = <TPreferenceKey extends UserPreferenceKey>(
  jsonString?: PreferenceValueJsonString,
) => {
  if (!jsonString?.trim()) return null;
  try {
    return JSON.parse(jsonString) as UserPreferenceValue<TPreferenceKey>;
  } catch (error) {
    // oxlint-disable-next-line no-console
    console.error('Failed to parse user preference JSON string:', error);
    return null;
  }
};

export const stringifyUserPreferenceValueJson = <TPreferenceKey extends UserPreferenceKey>(
  preferenceValue: UserPreferenceValue<TPreferenceKey>,
) => JSON.stringify(preferenceValue) as PreferenceValueJsonString;

/**
 * The form of a preference key that the API sees, both in the request URL and in storage.
 *
 * @remarks The key travels as a single URL path segment (`admin/userPreference/{preferenceKey}`),
 * so a `/` would split it in two and the request would 404. Percent-encoding does not help here:
 * a `%2F` in a *path* is not decoded back into a `/` before routing, unlike one in a query string.
 * So `/` is escaped instead, using `~` — unreserved in URLs, and left alone by `encodeURIComponent`.
 * `~` itself is escaped first, so distinct keys can never collapse onto the same escaped key.
 */
export const toApiPreferenceKey = (preferenceKey: UserPreferenceKey) =>
  preferenceKey.replaceAll('~', '~~').replaceAll('/', '~2F');
